import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { appPath } from '../../../constants';
import { dayDateFormat, defaultTimeZone, uiDateFormatShort } from '../../../constants/date';
import { confirmationMessages, alertMessages, labels } from '../../../constants/text';
import BackArrow from '../../common/ui/BackArrow';
import Loader from '../../common/ui/Loader';
import { getEventSchedule } from '../../../actions/events';
import { approveMultiDayJobAction, cancelJobAction } from '../../../actions/jobs';
import { editJobWorkerAction } from '../../../actions/jobWorkers';
import { getWorkerQuickView, clearWorkerQuickView } from '../../../actions/worker';
import { UIShowConfirm, UIShowAlert } from '../../../actions/ui';
import { getLanguagesList } from '../../../actions';
import { startSupportConversation, updateUnreadMessagesQuantity, updateUserConversationPreferences } from '../../../actions/conversations';
import { isJobCancelled, splitJobsByDate } from '../../../helpers/jobs';
import { formatPrice } from '../../../helpers/formatData';
import { getNowInTimeZone } from '../../../helpers/timezones';
import JobListSection from '../jobsList/JobListSection';
import EmptyListUi from '../jobsList/EmptyListUi';
import UsersService from '../../../services/UsersService';
import WorkerQuickView from '../../common/ui/QuickView/WorkerQuickView';
import QuickView from '../../common/ui/QuickView/QuickView';
import { jobWorkerStatus } from '../../../constants/jobs';
import EventActionsButtons from './EventActionsButtons';

class EventView extends Component {
  static propTypes = {
    eventSchedule: PropTypes.object.isRequired,
    languages: PropTypes.array.isRequired,
    getEventSchedule: PropTypes.func.isRequired,
    UIShowConfirm: PropTypes.func.isRequired,
    UIShowAlert: PropTypes.func.isRequired,
    approveMultiDayJobAction: PropTypes.func.isRequired,
    cancelJobAction: PropTypes.func.isRequired,
    editJobWorkerAction: PropTypes.func.isRequired,
    getWorkerQuickView: PropTypes.func.isRequired,
    clearWorkerQuickView: PropTypes.func.isRequired,
    getLanguagesList: PropTypes.func.isRequired,
    startSupportConversation: PropTypes.func.isRequired,
    updateUserConversationPreferences: PropTypes.func.isRequired,
    updateUnreadMessagesQuantity: PropTypes.func.isRequired
  };

  state = {
    isQuickViewOpen: false,
    signatureModalIsOpen: false,
    isLoading: true
  };

  componentDidMount () {
    this._loadData();
    if (!this.props.languages.length) {
      this.props.getLanguagesList();
    }
  }

  _loadData = () => {
    this.setState({ isLoading: true });
    const eventId = this.props.match.params.id;
    this.props.getEventSchedule(eventId).then(data => {
      this.setState({ isLoading: false });
      if (!data) {
        this.props.history.push(`/${appPath}/jobs`);
      }
    });
  };

  needAddHours = () => {
    const { eventSchedule: { jobs } } = this.props;
    for (let i = 0; i < jobs.length; i++) {
      const workers = jobs[i].workers;
      if (workers && workers.some(worker => worker.status === jobWorkerStatus.HIRED && _.isNull(worker.actual_hours))) {
        return true;
      }
    }
    return false;
  };

  approveHoursEvent = () => {
    const { eventSchedule: { id }, approveMultiDayJobAction } = this.props;
    if (this.needAddHours()) {
      this.props.UIShowAlert(alertMessages.approveJobNoHours);
    } else {
      if (UsersService.isAdmin()) {
        this.props.UIShowConfirm(confirmationMessages.approveJob, () => approveMultiDayJobAction(id).then(this._loadData));
      } else {
        this.setState({
          signatureModalIsOpen: true
        });
      }
    }
  };

  submitJobApprovalEmployer = signatureUrl => {
    const { approveMultiDayJobAction, eventSchedule } = this.props;
    approveMultiDayJobAction(eventSchedule.id, signatureUrl).then(() => {
      this.closeSignatureModal();
      this._loadData();
    });
  };

  closeSignatureModal = () => {
    this.setState({
      signatureModalIsOpen: false
    });
  };

  sortJobsList = () => {
    const { jobs } = this.props.eventSchedule;
    if (jobs.length === 0) {
      return [];
    } else {
      return _.orderBy(jobs, ['start_date', 'id']);
    }
  };

  navigateToCreateJob = () => {
    this.props.history.push(`/${appPath}/newjob`);
  };

  cancelJob = jobId => {
    const { cancelJobAction } = this.props;
    const userRole = UsersService.getUserRole();
    cancelJobAction(jobId, userRole).then(this._loadData);
  };

  editJobWorker = (workerId, workerData, job_id) => {
    const jobWorkerData = {
      job_id,
      ...workerData
    };
    this.props.editJobWorkerAction(workerId, jobWorkerData).then(this._loadData);
  };

  openQuickView = () => this.setState({ isQuickViewOpen: true });

  closeQuickView = () => {
    this.setState({ isQuickViewOpen: false });
    this.props.clearWorkerQuickView();
  };

  onQuickViewOpen = id => {
    this.openQuickView();
    this.props.getWorkerQuickView(id);
  };

  calcSumEstCost = () => {
    const { eventSchedule: { jobs } } = this.props;
    const estCostSum = jobs.reduce((sum, job) => sum + job.estCost, 0);
    return formatPrice(estCostSum);
  };

  hasEventHiredWorkers = () => {
    const { eventSchedule: { jobs } } = this.props;
    if (jobs.length > 0) {
      for (let i = 0; i < jobs.length; i++) {
        const workers = jobs[i].workers;
        if (workers && workers.some(worker => worker.status === jobWorkerStatus.HIRED)) {
          return true;
        }
      }
    }
    return false;
  };

  getEventHiredWorkers = () => {
    const { eventSchedule: { jobs } } = this.props;
    const hiredWorkers = [];
    if (jobs.length > 0) {
      for (let i = 0; i < jobs.length; i++) {
        const workers = jobs[i].workers;
        if (workers) {
          workers.forEach(worker => {
            if (worker.status === jobWorkerStatus.HIRED) {
              hiredWorkers.push(worker);
            }
          });
        }
      }
    }
    return hiredWorkers;
  };

  updateConversationMessagesCount = async (conversation_id, participant_id) => {
    const { updateUserConversationPreferences, updateUnreadMessagesQuantity } = this.props;
    await updateUserConversationPreferences(
      conversation_id,
      participant_id,
      {
        unread_count: 0
      }
    );
    return updateUnreadMessagesQuantity();
  };

  render () {
    const { isLoading } = this.state;
    const { eventSchedule } = this.props;
    const { start_date, end_date, invoice_number, invoice_url, invoice_date, eventSkillsLabels, eventSkillsIcons,
      total_payment, jobs } = eventSchedule;
    const eventDatesShift = `${moment(start_date).format(dayDateFormat)} - ${moment(end_date).format(dayDateFormat)}`;
    const jobsListSectionsByDate = splitJobsByDate(this.sortJobsList());
    const jobsListExists = jobsListSectionsByDate.length > 0;
    const eventSkills = eventSkillsLabels ? eventSkillsLabels.join(', ') : '';
    const eventHasFinalCost = total_payment || total_payment === 0;
    let estCostSum;
    if (!eventHasFinalCost) {
      estCostSum = this.calcSumEstCost();
    }
    const eventIsApprovedOrCancelled = jobs.every(job => job.is_approved || isJobCancelled(job.status));
    const allJobsAreCancelled = jobs.every(job => isJobCancelled(job.status));
    const lastJob = jobs[jobs.length - 1];
    const eventIsEnded = moment(end_date).isBefore(getNowInTimeZone(lastJob ? lastJob.time_zone_id : defaultTimeZone));
    const showApproveEventButton = eventIsEnded && !eventIsApprovedOrCancelled && this.hasEventHiredWorkers();
    return (
      <Fragment>
        <div className='job-details-wrapper'>
          <div className='top'>
            <div className='header-left'>
              <BackArrow
                history={this.props.history}
                location={this.props.location}
                defaultBackPath={`/${appPath}/jobs`}
              />
              <div className='mr-2 d-flex align-center'>
                { eventSkillsIcons && eventSkillsIcons.map((iconUrl, i) =>
                  <div className='skill-icon' style={{ backgroundImage: `url('${iconUrl}')` }} key={i} />
                )}
              </div>
              <div>
                <h3>{ eventSkills }</h3>
                <p className='start-date'>{ eventDatesShift }</p>
              </div>
            </div>
            { !isLoading && (
              <div>
                <div className='d-flex align-center'>
                  <p>{ eventHasFinalCost ? 'Final Cost' : 'Billing Estimate'}</p>
                  <h4 className='price'>{ eventHasFinalCost ? formatPrice(total_payment) : estCostSum }</h4>
                  { eventIsApprovedOrCancelled && (
                    <React.Fragment>
                      { allJobsAreCancelled ? (
                        <div className={`status-btn cancelled`}>Cancelled</div>
                      ) : (
                        <div className={`status-btn approved`}>Approved</div>
                      )}
                    </React.Fragment>
                  )}
                </div>
                { showApproveEventButton && (
                  <p className='need-approve-label'>
                    <span className='icon-wrapper exclamation-icon'>
                      <FontAwesomeIcon icon={faExclamationCircle} />
                    </span>
                    {labels.needApproveLabel}
                  </p>
                )}
                { invoice_number && (
                  <div className='invoice-info'>
                    Invoice Number: <a className='link' target='_blank' href={invoice_url}>{invoice_number}</a>
                  </div>
                )}
                { invoice_date && (
                  <div className='invoice-info'>
                    Invoice Date: {moment(invoice_date).format(uiDateFormatShort)}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className='tabs-wrapper'>
            <button className='tab active'>Jobs</button>
          </div>
          { isLoading ? <div className='loader-wrapper'><Loader /></div> : (
            <React.Fragment>
              <EventActionsButtons
                eventId={eventSchedule.id}
                hiredWorkers={this.getEventHiredWorkers()}
                showApproveEventButton={showApproveEventButton}
                approveHoursEvent={this.approveHoursEvent}
                signatureModalIsOpen={this.state.signatureModalIsOpen}
                closeSignatureModal={this.closeSignatureModal}
                submitJobApprovalEmployer={this.submitJobApprovalEmployer}
                UIShowConfirm={this.props.UIShowConfirm}
                updateConversationMessagesCount={this.updateConversationMessagesCount}
              />
              { jobsListExists ? (
                <div>
                  {jobsListSectionsByDate.map(section => (
                    <JobListSection
                      key={section.formattedDate}
                      formattedDate={section.formattedDate}
                      weekDay={section.weekDay}
                      jobsList={section.data}
                      history={this.props.history}
                      cancelJob={this.cancelJob}
                      UIShowConfirm={this.props.UIShowConfirm}
                      UIShowAlert={this.props.UIShowAlert}
                      isEventView
                      editJobWorker={this.editJobWorker}
                      onQuickViewOpen={this.onQuickViewOpen}
                    />
                  ))}
                </div>
              ) : (
                <EmptyListUi navigateToCreateJob={this.navigateToCreateJob} />
              )}
            </React.Fragment>
          )}
        </div>

        <QuickView
          isActive={this.state.isQuickViewOpen}
          onClose={this.closeQuickView}
          isLoading={this.props.isWorkerLoading}
        >
          <WorkerQuickView
            {...this.props.workerQuickView}
            languages={this.props.languages}
            messageWorker={this.props.startSupportConversation}
            updateConversationMessagesCount={this.updateConversationMessagesCount}
          />
        </QuickView>
      </Fragment>
    );
  }
}

const mapStateToProps = state => ({
  eventSchedule: state.events.eventSchedule,
  workerQuickView: state.worker.workerQuickView,
  isWorkerLoading: state.worker.isLoading,
  languages: state.languagesList
});

const mapDispatchToProps = {
  getEventSchedule,
  UIShowConfirm,
  UIShowAlert,
  approveMultiDayJobAction,
  cancelJobAction,
  editJobWorkerAction,
  getWorkerQuickView,
  clearWorkerQuickView,
  getLanguagesList,
  startSupportConversation,
  updateUserConversationPreferences,
  updateUnreadMessagesQuantity
};

export default connect(mapStateToProps, mapDispatchToProps)(EventView);



// WEBPACK FOOTER //
// ./src/components/app/eventView/index.js